import React, {useContext, useEffect, useMemo, useReducer, useState} from 'react'
import {
    CContainer,
    CRow,
    CCol,
    CCard,
    CCardHeader,
    CCardBody,
    CBadge,
    CButton,
    CForm,
    CFormInput,
    CFormSelect,
    CFormTextarea,
    CTooltip,
} from '@coreui/react'

// dnd-kit
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable'

import CIcon from '@coreui/icons-react'
import { cilPlus, cilSettings } from '@coreui/icons'

// servicios/modelos
import EmployeesService from '../services/employees.service'
import AgendaService from '../services/agenda.service'
import Columna from '../models/Columna'
import Tarea from '../models/Tarea'
import Column from "../components/agenda/Columna";
import TaskModal from "../components/agenda/TareaModal";
import TaskViewModal from "../components/agenda/TareaModalVisor";
import SortableColumn from "../components/agenda/OrdenableColumna";
import ColumnManagerModal from "../components/agenda/ColumnaModal";
import {ACTIONS} from "../config/constants";
import {StateContext} from "../components/context/StateProvider";
import Toasts from "../components/notificacion/Toasts";
import UsuariosService from "../services/usuarios.service";

// ---------- Constants ----------
const LS_KEY = 'agenda_board'

/**
 * @param {BoardState} state
 * @param {{type:string,payload:any}} action
 * @returns {BoardState}
 */
function boardReducer(state, action) {
    switch (action.type) {
        case ACTIONS.ADD_TASK: {
            const task = /** @type {Task} */ (action.payload)
            const safeTask = { comments: [], labels: [], description: '', assignee: '', priority: 'medio', ...task }
            const tasks = { ...state.tasks, [safeTask.id]: safeTask }
            const col = state.columns[safeTask.columnId]
            if (!col) return state
            return {
                ...state,
                tasks,
                columns: {
                    ...state.columns,
                    [col.id]: { ...col, taskIds: [...col.taskIds, safeTask.id] },
                },
            }
        }
        case ACTIONS.UPDATE_TASK: {
            const task = /** @type {Task} */ (action.payload)
            return { ...state, tasks: { ...state.tasks, [task.id]: task } }
        }
        case ACTIONS.DELETE_TASK: {
            const { taskId } = action.payload
            const task = state.tasks[taskId]
            if (!task) return state
            const col = state.columns[task.columnId]
            const updatedCol = { ...col, taskIds: col.taskIds.filter((id) => id !== taskId) }
            const tasks = { ...state.tasks }
            delete tasks[taskId]
            return { ...state, tasks, columns: { ...state.columns, [col.id]: updatedCol } }
        }
        case ACTIONS.MOVE_TASK: {
            const { taskId, fromColumnId, toColumnId, toIndex } = action.payload
            const task = state.tasks[taskId]
            if (!task) return state

            const fromCol = state.columns[fromColumnId]
            const toCol = state.columns[toColumnId]
            if (!fromCol || !toCol) return state

            const fromIds = fromCol.taskIds.filter((id) => id !== taskId)
            const destIds = [...toCol.taskIds]
            const idx = typeof toIndex === 'number' ? Math.max(0, Math.min(toIndex, destIds.length)) : destIds.length
            destIds.splice(idx, 0, taskId)

            return {
                ...state,
                columns: {
                    ...state.columns,
                    [fromCol.id]: { ...fromCol, taskIds: fromIds },
                    [toCol.id]: { ...toCol, taskIds: destIds },
                },
                tasks: { ...state.tasks, [taskId]: { ...task, columnId: toColumnId } },
            }
        }
        case ACTIONS.SET_TASK_ORDER: {
            const { columnId, taskIds } = action.payload
            const col = state.columns[columnId]
            if (!col) return state
            return { ...state, columns: { ...state.columns, [columnId]: { ...col, taskIds } } }
        }
        case ACTIONS.ADD_COLUMN: {
            const { id, name } = action.payload
            if (!id || state.columns[id]) return state
            return {
                ...state,
                columns: { ...state.columns, [id]: { id, name: name || id, taskIds: [] } },
                columnOrder: [...state.columnOrder, id],
            }
        }
        case ACTIONS.RENAME_COLUMN: {
            const { id, name } = action.payload
            const col = state.columns[id]
            if (!col) return state
            return { ...state, columns: { ...state.columns, [id]: { ...col, name } } }
        }
        case ACTIONS.DELETE_COLUMN: {
            const { id, fallback = 'backlog', dropStrategy = 'move' } = action.payload
            if (!state.columns[id]) return state
            if (state.columnOrder.length <= 1) return state

            const newColumns = { ...state.columns }
            const removed = newColumns[id]
            delete newColumns[id]
            let newTasks = { ...state.tasks }

            if (dropStrategy === 'delete') {
                for (const tid of removed.taskIds) delete newTasks[tid]
            } else {
                const fb = newColumns[fallback] || state.columns[fallback]
                const fbTaskIds = [...(fb?.taskIds || [])]
                for (const tid of removed.taskIds) {
                    fbTaskIds.push(tid)
                    newTasks[tid] = { ...newTasks[tid], columnId: fb.id }
                }
                newColumns[fb.id] = { ...fb, taskIds: fbTaskIds }
            }

            return {
                ...state,
                columns: newColumns,
                columnOrder: state.columnOrder.filter((c) => c !== id),
                tasks: newTasks,
            }
        }
        case ACTIONS.SET_COLUMN_ORDER: {
            const { order } = action.payload
            if (!Array.isArray(order) || !order.length) return state
            return { ...state, columnOrder: order }
        }
        case ACTIONS.INIT_FROM_API: {
            const { columns, columnOrder, tasks } = action.payload
            return {
                columns: columns ?? state.columns,
                columnOrder: columnOrder ?? state.columnOrder,
                tasks: tasks ?? state.tasks,
            }
        }
        default:
            return state
    }
}

const Agenda = () => {
    const {updateState} = useContext(StateContext)
    const [state, dispatch] = useReducer(boardReducer, {
        columns: {},
        columnOrder: [],
        tasks: {},
    });
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [employees, setEmployees] = useState([])


    const [taskModalOpen, setTaskModalOpen] = useState(false)
    const [editingTask, setEditingTask] = useState(/** @type {Task|null} */ (null))
    const [columnsModalOpen, setColumnsModalOpen] = useState(false)

    // NUEVO: estado para vista en solo lectura
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [viewingTask, setViewingTask] = useState(/** @type {Task|null} */ (null))

    // Carga inicial desde API (fallback a LS si falla)
    useEffect(() => {
        ;(async () => {
            setLoading(true)
            setError(null)
            try {
                const cols = await AgendaService.listarColumnas()
                const columns = {}
                const columnOrder = []
                const tasks = {}
                cols.forEach((c) => {

                    const col = c instanceof Columna ? c : Columna.from(c)
                    columns[col.id] = { id: col.id, name: col.name, taskIds: col.taskIds ?? [] }

                    if (Array.isArray(c.tasks)) {
                        c.tasks.forEach((t) => {
                            const tt = t instanceof Tarea ? t : Tarea.from(t)
                            tasks[tt.id] = tt
                        })
                    }

                    columnOrder.push(col.id)
                })
                dispatch({ type: ACTIONS.INIT_FROM_API, payload: { columns, columnOrder, tasks: tasks } })
            } catch (e) {
                setError('No se pudieron cargar las columnas')
            } finally {
                setLoading(false)
            }
        })();
        (async () => {
           try {
                const list = await UsuariosService.listarUsuariosMap()
                    setEmployees(Array.isArray(list) ? list : [])
                    } catch (e) {
                console.error('Error cargando empleados', e)
                setEmployees([])
                }
            })();
    }, [])

    const employeesById = useMemo(() => Object.fromEntries(employees.map((e) => [String(e.id), e])), [employees])

    // Persistencia local
    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(state))
        } catch {}
    }, [state])

    const tasksByColumn = useMemo(() => {
        /** @type {Record<string, Task[]>} */
        const result = {}
        for (const colId of state.columnOrder) {
            const col = state.columns[colId]
            result[colId] = col.taskIds.map((id) => state.tasks[id]).filter(Boolean)
        }
        return result
    }, [state])

    // dnd-kit sensors
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
        useSensor(KeyboardSensor)
    )

    // Drag state for overlay
    const [activeTask, setActiveTask] = useState(null)
    const [activeColumn, setActiveColumn] = useState(null)

    const onDragStart = (event) => {
        const { active } = event
        const data = active.data.current
        if (data?.type === 'task') setActiveTask(state.tasks[data.taskId])
        if (data?.type === 'column') setActiveColumn(state.columns[data.columnId])
    }

    const onDragEnd = (event) => {
        const { active, over } = event
        setActiveTask(null)
        setActiveColumn(null)
        if (!over) return

        const activeData = active.data.current
        const overData = over.data.current

        // --- Column reorder ---
        if (activeData?.type === 'column') {
            const oldIndex = state.columnOrder.indexOf(active.id)
            const newIndex = state.columnOrder.indexOf(over.id)
            if (oldIndex !== newIndex) {
                const prev = state
                const newOrder = arrayMove(state.columnOrder, oldIndex, newIndex)
                dispatch({ type: ACTIONS.SET_COLUMN_ORDER, payload: { order: newOrder } })
                ;(async () => {
                    try {
                        await AgendaService.reordenarColumnas(newOrder)
                    } catch {
                        dispatch({ type: ACTIONS.INIT_FROM_API, payload: prev })
                    }
                })()
            }
            return
        }

        // --- Task drag ---
        if (activeData?.type === 'task') {
            const fromColumnId = activeData.columnId
            let toColumnId = fromColumnId
            let toIndex

            if (overData?.type === 'task') {
                toColumnId = state.tasks[over.id].columnId
                const destIds = state.columns[toColumnId].taskIds
                toIndex = destIds.indexOf(over.id)
            } else if (overData?.type === 'column') {
                toColumnId = overData.columnId
                toIndex = state.columns[toColumnId].taskIds.length
            } else {
                const maybeTask = state.tasks[over.id]
                if (maybeTask) {
                    toColumnId = maybeTask.columnId
                    const destIds = state.columns[toColumnId].taskIds
                    toIndex = destIds.indexOf(over.id)
                }
            }

            if (fromColumnId === toColumnId) {
                const ids = state.columns[fromColumnId].taskIds
                const oldIndex = ids.indexOf(active.id)
                const newIndex = typeof toIndex === 'number' ? toIndex : ids.length - 1
                if (oldIndex !== newIndex) {
                    const newOrder = arrayMove(ids, oldIndex, newIndex)
                    const prev = state
                    dispatch({ type: ACTIONS.SET_TASK_ORDER, payload: { columnId: fromColumnId, taskIds: newOrder } })
                    ;(async () => {
                        try {
                            await AgendaService.reordenarTarea(fromColumnId, newOrder)
                        } catch {
                            dispatch({ type: ACTIONS.INIT_FROM_API, payload: prev })
                        }
                    })()
                }
            } else {
                const prev = state
                dispatch({
                    type: ACTIONS.MOVE_TASK,
                    payload: { taskId: active.id, fromColumnId, toColumnId, toIndex },
                })
                ;(async () => {
                    try {
                        await AgendaService.moverTarea(active.id, toColumnId, toIndex)
                    } catch {
                        dispatch({ type: ACTIONS.INIT_FROM_API, payload: prev })
                    }
                })()
            }
        }
    }

    const loadAndViewTask = async (t) => {
        setViewingTask(t)
        setViewModalOpen(true)
    }

    if (loading) {
        return (
            <CContainer fluid className="py-3">
                <div className="text-muted">Cargando…</div>
            </CContainer>
        )
    }

    return (
        <CContainer fluid className="py-3">
            {error && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center">
                    <span>{error}</span>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => window.location.reload()}>
                        Reintentar
                    </button>
                </div>
            )}

            {!error && (
                <>
                    <CRow className="mb-2 align-items-center">
                        <CCol className="text-end d-flex justify-content-end gap-2">
                            <CButton color="secondary" onClick={() => setColumnsModalOpen(true)}>
                                <CIcon icon={cilSettings} className="me-1" />
                                Columnas
                            </CButton>
                            <CButton
                                color="primary"
                                onClick={() => {
                                    setEditingTask(null)
                                    setTaskModalOpen(true)
                                }}
                            >
                                <CIcon icon={cilPlus} className="me-2" />
                                Nueva tarea
                            </CButton>
                        </CCol>
                    </CRow>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                        <SortableContext items={state.columnOrder} strategy={rectSortingStrategy}>
                            <CRow className="g-3">
                                {state.columnOrder.map((colId) => (
                                    <SortableColumn key={colId} column={state.columns[colId]}>
                                        <Column
                                            column={state.columns[colId]}
                                            tasks={tasksByColumn[colId] || []}
                                            employeesById={employeesById}
                                            onOpenTask={async (t) => {
                                                setEditingTask(t)
                                                setTaskModalOpen(true)
                                            }}
                                            onDeleteTask={(id) => {
                                                const prev = state
                                                dispatch({ type: ACTIONS.DELETE_TASK, payload: { taskId: id } })
                                                ;(async () => {
                                                    try {
                                                        await AgendaService.eliminarTarea(id)
                                                    } catch {
                                                        dispatch({ type: ACTIONS.INIT_FROM_API, payload: prev })
                                                        const toasts = Toasts.generateToast('No se pudo eliminar la tarea', 'error');
                                                        updateState({toasts: [...state.toasts, toasts]})
                                                    }
                                                })()
                                            }}
                                            onViewTask={loadAndViewTask}
                                        />
                                    </SortableColumn>
                                ))}
                            </CRow>
                        </SortableContext>

                        <DragOverlay>
                            {activeTask ? (
                                <div className="bg-white rounded shadow p-2 border" style={{ maxWidth: 320 }}>
                                    {activeTask.title}
                                </div>
                            ) : null}
                            {activeColumn ? (
                                <div className="bg-white rounded shadow p-2 border" style={{ width: 320 }}>
                                    <div className="fw-semibold">{activeColumn.name}</div>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>

                    <TaskModal
                        visible={taskModalOpen}
                        onClose={() => {
                            setTaskModalOpen(false)
                            setEditingTask(null)
                        }}
                        onSave={(task) => {
                            const exists = Boolean(state.tasks[task.id])
                            const prev = state
                            // Optimista
                            dispatch({ type: exists ? ACTIONS.UPDATE_TASK : ACTIONS.ADD_TASK, payload: task })
                            setTaskModalOpen(false)
                            setEditingTask(null)
                            ;(async () => {
                                try {
                                    if (exists) {
                                        const saved = await AgendaService.actualizarTarea(task instanceof Tarea ? task : new Tarea(task))
                                        dispatch({ type: ACTIONS.UPDATE_TASK, payload: saved })
                                    } else {
                                        const created = await AgendaService.crearTarea(task instanceof Tarea ? task : new Tarea(task))
                                        dispatch({ type: ACTIONS.UPDATE_TASK, payload: created })
                                    }
                                } catch {
                                    dispatch({ type: ACTIONS.INIT_FROM_API, payload: prev })
                                    const toasts = Toasts.generateToast('No se pudo guardar la tarea', 'error');
                                    updateState({toasts: [...state.toasts, toasts]})
                                }
                            })()
                        }}
                        task={editingTask}
                        defaultColumnId={state.columnOrder[0]}
                        columnOptions={state.columnOrder.map((id) => ({ value: id, label: state.columns[id].name }))}
                    />

                    {/* Modal de visualización */}
                    <TaskViewModal
                        visible={viewModalOpen}
                        task={viewingTask}
                        onClose={() => {
                            setViewModalOpen(false)
                            setViewingTask(null)
                        }}
                        onEdit={(t) => {
                            setViewModalOpen(false)
                            setViewingTask(null)
                            setEditingTask(t)
                            setTaskModalOpen(true)
                        }}
                    />

                    <ColumnManagerModal
                        visible={columnsModalOpen}
                        onClose={() => setColumnsModalOpen(false)}
                        state={state}
                        dispatch={dispatch}
                    />
                </>
            )}
        </CContainer>
    )
}

export default Agenda
